/** Shared with index.html inline boot scripts — one auto-reload per session. */
export const BOOT_AUTO_RELOAD_KEY = '_mr';

/** One script-refetch self-heal attempt per boot failure (inline watchdog). */
export const BOOT_SELFHEAL_KEY = 'maestro_selfheal';

/** Rolling window auto-heal budget (mirrors index.html). */
export const BOOT_HEAL_LIMIT_KEY = 'maestro_heal_limit';
export const BOOT_VERSION_HEAL_KEY = 'maestro_mver_healed';
export const BOOT_HEAL_MAX = 2;
export const BOOT_HEAL_WINDOW_MS = 600_000;

export type HealLimitState = { start: number; count: number };

export function readHealLimit(storage: Pick<Storage, 'getItem'>): HealLimitState {
  try {
    const raw = storage.getItem(BOOT_HEAL_LIMIT_KEY);
    if (!raw) return { start: 0, count: 0 };
    return JSON.parse(raw) as HealLimitState;
  } catch {
    return { start: 0, count: 0 };
  }
}

export function autoHealBudgetRemaining(
  storage: Pick<Storage, 'getItem'>,
  now = Date.now(),
): boolean {
  const lim = readHealLimit(storage);
  if (!lim.start || now - lim.start > BOOT_HEAL_WINDOW_MS) return true;
  return lim.count < BOOT_HEAL_MAX;
}

export function consumeAutoHealBudget(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now = Date.now(),
): number {
  let lim = readHealLimit(storage);
  if (!lim.start || now - lim.start > BOOT_HEAL_WINDOW_MS) {
    lim = { start: now, count: 0 };
  }
  lim.count += 1;
  storage.setItem(BOOT_HEAL_LIMIT_KEY, JSON.stringify(lim));
  return lim.count;
}

/** Only the Vite entry chunk — never ipc:// plugin probes or third-party scripts. */
export function isAppEntryChunkTarget(
  el: EventTarget | null | undefined,
  pageHref: string,
): boolean {
  const node = el as { tagName?: string; src?: string } | null | undefined;
  if (!node || node.tagName !== 'SCRIPT') return false;
  const url = node.src || '';
  if (!url || /^ipc:/i.test(url)) return false;
  try {
    const u = new URL(url, pageHref);
    if (u.protocol === 'ipc:') return false;
    if (u.origin !== new URL(pageHref).origin) return false;
    return /\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(u.pathname);
  } catch {
    return false;
  }
}

export function bootSelfHealAlreadyAttempted(storage: Pick<Storage, 'getItem'>): boolean {
  return storage.getItem(BOOT_SELFHEAL_KEY) === '1';
}

export function markBootSelfHealAttempted(storage: Pick<Storage, 'setItem'>): void {
  storage.setItem(BOOT_SELFHEAL_KEY, '1');
}

export function clearBootSelfHealFlag(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(BOOT_SELFHEAL_KEY);
}

/** Same-origin Vite entry module only (not ipc:// or modulepreload noise). */
export function collectSameOriginAssetUrls(
  doc: Pick<Document, 'querySelectorAll'>,
  pageHref: string,
): string[] {
  const pageOrigin = new URL(pageHref).origin;
  const urls: string[] = [];
  doc.querySelectorAll('script[src][type="module"]').forEach((el) => {
    const node = el as HTMLScriptElement;
    if (!isAppEntryChunkTarget(node, pageHref)) return;
    const raw = node.src;
    if (!raw) return;
    try {
      const u = new URL(raw, pageHref);
      if (u.origin === pageOrigin) urls.push(u.href);
    } catch {
      /* ignore malformed URLs */
    }
  });
  return urls;
}

export async function refetchSameOriginAssets(
  doc: Pick<Document, 'querySelectorAll'>,
  pageHref: string,
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  const urls = collectSameOriginAssetUrls(doc, pageHref);
  await Promise.all(
    urls.map((url) => fetchFn(url, { cache: 'reload' }).catch(() => undefined)),
  );
}

/** Cache-busted navigation using shell-native ?b= (matches webview_cache.rs). */
export function cacheBustedReload(href = window.location.href): void {
  try {
    const url = new URL(href);
    url.searchParams.delete('_mb');
    url.searchParams.set('b', String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

/** Strip heal query params after successful boot so shell guard won't re-navigate. */
export function stripBootHealParams(href = window.location.href): string | null {
  try {
    const url = new URL(href);
    if (!url.searchParams.has('b') && !url.searchParams.has('_mb')) return null;
    url.searchParams.delete('b');
    url.searchParams.delete('_mb');
    const qs = url.searchParams.toString();
    const clean = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
    window.history.replaceState(null, '', clean);
    return clean;
  } catch {
    return null;
  }
}

/** At most one automatic boot reload per session (checked before navigating). */
export function tryBootAutoReload(): boolean {
  try {
    if (sessionStorage.getItem(BOOT_AUTO_RELOAD_KEY) === '1') return false;
    sessionStorage.setItem(BOOT_AUTO_RELOAD_KEY, '1');
    cacheBustedReload();
    return true;
  } catch {
    return false;
  }
}
